/**
 * ============================================================================
 *  File        : frontend/src/pages/CaseOverviewPage.tsx
 *  Module      : pages
 *  Purpose     : /cases/:id 案件總覽（spec §8.2）— Card 抬頭 + 品項 Table +
 *                lifecycle 推進控制（設定会場 set-venue / 出荷確定 ship）。
 *  Author      : EF Engineering
 *  Created     : 2026-05-31
 *  Updated     : 2026-06-01
 *  License     : Proprietary - ECO FAITH Platform v3.0
 * ============================================================================
 *
 *  lifecycle 控制（執行中 binary 已驗 2026-06-01）：
 *    - 設定会場：venue org 下拉（org_type=venue）+ 確定 → PUT /cases/:id/venue
 *      {venue_org_id}。兩方 org 齊備後 workflow 推進 Draft → DetailAccepted。
 *      已設会場後唯讀顯示，下拉/按鈕停用（venue 只設一次）。
 *    - 出荷確定：DetailAccepted 時顯示 → POST /cases/:id/ship（→ Shipped）。
 *  狀態權威來源 = GET /cases/:id/workflow-state（allowed_next 判定可推進性）。
 *  RBAC：set-venue = marketplace.cases.update；ship = logistics.shipments.manage
 *        （後端 gate；前端以 usePermissions 同步隱藏無權限入口）。
 */

import { useMemo, useState, type ReactElement } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useParams } from 'react-router-dom'
import { getCase, listVenueOrgs, setVenue } from '../api/cases'
import { getWorkflowState, shipCase } from '../api/workflow'
import { ApiError } from '../api/client'
import { usePermissions } from '../hooks/usePermissions'
import { PERMISSIONS } from '../lib/permissions'
import { Badge } from '../components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card'
import { Skeleton } from '../components/ui/skeleton'
import { Button } from '../components/ui/button'
import { toast } from '../components/ui/sonner'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table'

interface PageProps {
  t: (key: string) => string
}

/** 出荷確定可推進的前置狀態（workflow allowed_next 含 Shipped）。 */
const SHIPPED_NEXT = 'Shipped'

function errDetail(error: unknown): string {
  return error instanceof ApiError ? `（${error.message}）` : ''
}

export function CaseOverviewPage({ t }: PageProps): ReactElement {
  const params = useParams()
  const id = params.id ?? ''
  const queryClient = useQueryClient()
  const { can } = usePermissions()
  const canSetVenue = can(PERMISSIONS.venueSet)
  const canShip = can(PERMISSIONS.shipConfirm)

  const query = useQuery({
    queryKey: ['case', id],
    queryFn: () => getCase(id),
    enabled: id !== '',
  })
  const workflowQuery = useQuery({
    queryKey: ['case', id, 'workflow-state'],
    queryFn: () => getWorkflowState(id),
    enabled: id !== '',
  })

  if (query.isPending) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    )
  }

  if (query.isError || !query.data) {
    return (
      <p className="text-sm text-destructive">
        {t('case.detail.loadError')}
        {query.error instanceof ApiError ? `（${query.error.message}）` : ''}
      </p>
    )
  }

  const { items, ...c } = query.data
  const allowedNext = workflowQuery.data?.allowed_next ?? []
  const workflowState = workflowQuery.data?.workflow_state ?? c.workflow_state ?? null
  const canAdvanceShip = allowedNext.includes(SHIPPED_NEXT)
  const venueIsSet = c.venue_org_id !== null && c.venue_org_id !== undefined

  const refresh = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['case', id] })
    void queryClient.invalidateQueries({ queryKey: ['case', id, 'workflow-state'] })
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>{t('case.detail.headerCard')}</CardTitle>
          <div className="flex items-center gap-2">
            <Badge variant="secondary">{c.status}</Badge>
            <Button asChild size="sm">
              <Link to={`/cases/${c.id}/settlements/p1`}>{t('case.detail.goP1')}</Link>
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm sm:grid-cols-3">
            <Field label={t('field.caseNo')} value={c.case_no} />
            <Field label={t('field.caseTitle')} value={c.title} />
            <Field label={t('field.workflowState')} value={workflowState ?? '—'} />
            <Field label={t('field.currencyCode')} value={c.currency_code} />
            <Field
              label={t('case.detail.venue')}
              value={venueIsSet ? `org #${c.venue_org_id}` : t('case.detail.venueUnset')}
            />
          </dl>
        </CardContent>
      </Card>

      {/* lifecycle 推進：設定会場 + 出荷確定 */}
      <Card>
        <CardHeader>
          <CardTitle>{t('case.lifecycle.title')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* 設定会場 */}
          <div className="space-y-2">
            <p className="text-sm font-medium">{t('case.venue.label')}</p>
            {venueIsSet ? (
              <p className="text-sm text-muted-foreground" data-testid="venue-current">
                {t('case.venue.alreadySet').replace('{id}', String(c.venue_org_id))}
              </p>
            ) : !canSetVenue ? (
              <Badge variant="outline">{t('rbac.forbidden')}</Badge>
            ) : (
              <SetVenuePanel t={t} caseId={id} onDone={refresh} />
            )}
          </div>

          {/* 出荷確定 */}
          <div className="space-y-2">
            <p className="text-sm font-medium">{t('case.ship.label')}</p>
            {!canShip ? (
              <Badge variant="outline">{t('rbac.forbidden')}</Badge>
            ) : canAdvanceShip ? (
              <ShipPanel t={t} caseId={id} onDone={refresh} />
            ) : (
              <p className="text-sm text-muted-foreground">{t('case.ship.notReady')}</p>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>
            {t('case.detail.itemsCard')}（{items.length}）
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border border-border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('case.detail.lineNo')}</TableHead>
                  <TableHead>{t('field.itemName')}</TableHead>
                  <TableHead className="text-right">{t('field.estimatedWeight')}</TableHead>
                  <TableHead className="text-right">{t('field.minUnitPrice')}</TableHead>
                  <TableHead className="text-right">{t('field.quantityCount')}</TableHead>
                  <TableHead>{t('field.uom')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell>{item.line_no}</TableCell>
                    <TableCell className="font-medium">{item.item_name}</TableCell>
                    <TableCell className="text-right">{item.estimated_weight_kg ?? '—'}</TableCell>
                    <TableCell className="text-right">{item.min_unit_price ?? '—'}</TableCell>
                    <TableCell className="text-right">{item.quantity ?? '—'}</TableCell>
                    <TableCell>{item.uom}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

// ---- 設定会場面板 ----------------------------------------------------------

function SetVenuePanel({
  t,
  caseId,
  onDone,
}: {
  t: (key: string) => string
  caseId: string
  onDone: () => void
}): ReactElement {
  const [venueOrgId, setVenueOrgId] = useState('')
  const venuesQuery = useQuery({
    queryKey: ['venue-orgs'],
    queryFn: () => listVenueOrgs(),
  })
  const venues = useMemo(() => venuesQuery.data ?? [], [venuesQuery.data])

  const mutation = useMutation({
    mutationFn: () => setVenue(caseId, Number(venueOrgId)),
    onSuccess: () => {
      toast.success(t('case.venue.success'))
      onDone()
    },
    onError: (error) => toast.error(`${t('case.venue.error')}${errDetail(error)}`),
  })

  return (
    <form
      className="flex max-w-md flex-wrap items-end gap-2"
      onSubmit={(e) => {
        e.preventDefault()
        if (venueOrgId) mutation.mutate()
      }}
    >
      <div className="min-w-[12rem] flex-1 space-y-1">
        <span className="text-xs text-muted-foreground">{t('case.venue.select')}</span>
        <Select value={venueOrgId} onValueChange={setVenueOrgId}>
          <SelectTrigger data-testid="venue-select" aria-label={t('case.venue.select')}>
            <SelectValue placeholder={t('case.venue.placeholder')} />
          </SelectTrigger>
          <SelectContent>
            {venues.map((v) => (
              <SelectItem key={v.id} value={String(v.id)}>
                {v.name}（org #{v.id}）
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <Button type="submit" data-testid="set-venue" disabled={mutation.isPending || !venueOrgId}>
        {mutation.isPending ? t('case.venue.saving') : t('case.venue.confirm')}
      </Button>
    </form>
  )
}

// ---- 出荷確定面板 ----------------------------------------------------------

function ShipPanel({
  t,
  caseId,
  onDone,
}: {
  t: (key: string) => string
  caseId: string
  onDone: () => void
}): ReactElement {
  const mutation = useMutation({
    mutationFn: () => shipCase(caseId),
    onSuccess: () => {
      toast.success(t('case.ship.success'))
      onDone()
    },
    onError: (error) => toast.error(`${t('case.ship.error')}${errDetail(error)}`),
  })

  return (
    <div className="space-y-1">
      <Button
        type="button"
        data-testid="ship-confirm"
        disabled={mutation.isPending}
        onClick={() => mutation.mutate()}
      >
        {mutation.isPending ? t('case.ship.shipping') : t('case.ship.confirm')}
      </Button>
      <p className="text-xs text-muted-foreground">{t('case.ship.hint')}</p>
    </div>
  )
}

function Field({ label, value }: { label: string; value: string }): ReactElement {
  return (
    <div>
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="font-medium">{value}</dd>
    </div>
  )
}
